import React from "react";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { Box, Typography, Alert, Stack, Paper, CardContent } from "@mui/material";
import { useGetMyProfileQuery } from "../../../services/ProfileService";
import { ProfileView } from "../../../services/ProfileService/types/profileTypes";
import { SearchBox } from '../components/views/SearchBox';
import { ProfileCreateDialog } from '../components/dialogs/ProfileCreateDialog';
import { PageLoading as Loading } from '../../common/components';
import { PageCard } from "../../common/components/styled-ui-elements";
import { ProfileInfoSection } from "../components/sections/ProfileInfoSection";
import { CenterAccessSection } from "../components/sections/CenterAccessSection";
import { ClaimsAccessSection } from "../components/sections/ClaimsAccessSection";
import { useProfileData } from "../hooks/useProfileData";
import { THEME_COLORS, hasAdminAccess } from "../utils/profileUtils";

// Header Component
const PageHeader: React.FC<{ profile: ProfileView | undefined }> = ({ profile }) => {
  const showAdminActions = hasAdminAccess(profile);

  return (
    <Paper elevation={1} sx={{ p: 1, mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h6" component="h1" sx={{ mb: 0, fontWeight: 600, fontSize: '1.1rem' }}>
            My Access Profile
          </Typography>
          <Typography variant="body2" color={THEME_COLORS.text.secondary} sx={{ fontSize: '0.8rem' }}>
            Manage role, center access, and claim permissions
            {profile?.organization && (
              <span> | {profile.organization}</span>
            )}
          </Typography>
        </Box>
        {showAdminActions && (
          <Stack direction="row" spacing={2} alignItems="center">
            <ProfileCreateDialog />
            <SearchBox />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const MyAccess: React.FC = () => {
  const { data: profile, isLoading, isError } = useGetMyProfileQuery();
  const {
    role,
    transformedClaimsAccess,
    transformedAvailableClaims,
    transformedAvailableCenters,
    transformedCenterAccess,
    handleRoleChange,
    handleAddClaimAccess,
    handleDeleteClaimAccess,
    handleAddCenterAccess,
    handleDeleteCenterAccess
  } = useProfileData({ profile });

  if (isLoading) return <Loading />;
  if (isError) return (
    <Alert severity="error" sx={{ m: 2 }}>
      Failed to load profile. Please try again.
    </Alert>
  );

  return (
    <AuthenticatedTemplate>
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: THEME_COLORS.background,
        py: 1,
        px: 1.5
      }}>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <PageHeader profile={profile} />
            
            <CardContent sx={{ p: 2, backgroundColor: THEME_COLORS.card }}>
              {profile && (
                <Stack spacing={2}>
                  {/* Profile Information */}
                  <ProfileInfoSection 
                    profile={profile} 
                    role={role} 
                    onRoleChange={handleRoleChange}
                  />

                  {/* Center Access and Claims Access - Side by Side */}
                  <Stack direction="row" spacing={2}>
                    <CenterAccessSection
                      centers={transformedCenterAccess}
                      availableCenters={transformedAvailableCenters}
                      onAddCenter={profile?.isAdmin ? handleAddCenterAccess : undefined}
                      onDeleteCenter={profile?.isAdmin ? handleDeleteCenterAccess : undefined}
                    />
                    <ClaimsAccessSection
                      claims={transformedClaimsAccess}
                      availableClaims={transformedAvailableClaims}
                      onAddClaim={profile?.isAdmin ? handleAddClaimAccess : undefined}
                      onDeleteClaim={profile?.isAdmin ? handleDeleteClaimAccess : undefined}
                    />
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </PageCard>
        </Box>
      </Box>
    </AuthenticatedTemplate>
  );
};

export default MyAccess;

